import { useApolloClient } from "@apollo/client";
import { MenuItem, Text } from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import _ from "lodash";
import React from "react";
import { WithUserPermissions } from "src/components/Permissions/WithUserPermissions";
import * as FSQ from "src/generated/hooks/form-service/queries";
import { useAvelaToast } from "src/hooks/useAvelaToast";
import { useConfirmationDialog } from "src/hooks/useConfirmationDialog";
import { asyncForEach } from "src/services/asyncHelpers";
import { maybePluralize } from "src/services/format";
import { FormTemplateSettings } from "src/types/formTemplate/v2";
import { OfferError } from "../errors";
import {
  FETCH_OFFER_WAITLIST_STATUS,
  GET_ASSIGNED_FORMS_BY_FORM_ID,
} from "../graphql/queries";
import type { FormSchoolStatus, SchoolFormId } from "../types";
import { canDeclineOffers } from "../validations/declineOffers";
import { trackFormAction, trackFormActionError } from "./trackFormAction";

const DECLINE_BULK_FORM_SCHOOL_OFFER_MAX_INPUT_SIZE = 500;

interface Props {
  formTemplateSettings: Pick<FormTemplateSettings, "allowDeclineAcceptedOffer">;
  selectedFormSchoolStatuses: FormSchoolStatus[];
  selectedFormSchools: SchoolFormId[];
  onRefetch?: () => void;
  menuLabel?: string;
  isBulkAction?: boolean;
}

const confirmationBodyText = (count: number) =>
  maybePluralize(
    count,
    "Declining this offer will prevent or undo the family's acceptance.",
    "Declining these offers will prevent or undo the families' acceptance."
  );

const confirmButtonLabel = (count: number) =>
  maybePluralize(count, "Yes, decline offer", "Yes, decline offers");

export const DeclineOffer = ({
  formTemplateSettings,
  selectedFormSchoolStatuses,
  selectedFormSchools,
  onRefetch,
  menuLabel = "Decline offers",
  isBulkAction = false,
}: Props) => {
  const client = useApolloClient();
  const queryClient = useQueryClient();

  const { mutateAsync: declineBulkFormSchoolOffer } = FSQ.useBulkDeclineOffer();

  const toast = useAvelaToast();

  const { confirm, confirmationDialog } = useConfirmationDialog({
    header: null,
    body: null,
    cancelButton: {
      label: "Cancel",
    },
    translate: true,
  });

  const handleOnClick = async (evt: React.MouseEvent) => {
    const schoolForms = selectedFormSchoolStatuses;
    const count = schoolForms.length;
    const trackArgs = {
      actionType: "DECLINE_OFFER" as const,
      bulk: isBulkAction,
      count,
      async: false,
    };

    const offerNoun = maybePluralize(count, "offer");

    trackFormAction({ verb: "Clicked", ...trackArgs });

    if (
      await confirm({
        header: <Text>{`Decline ${count} ${offerNoun}?`}</Text>,
        body: <Text>{confirmationBodyText(count)}</Text>,
        confirmButton: {
          label: confirmButtonLabel(count),
          colorScheme: "blue",
        },
      })
    ) {
      trackFormAction({ verb: "Confirmed", ...trackArgs });

      const loadingToastId = toast({
        title: `Working hard`,
        description: `Declining ${count} ${offerNoun}...`,
        status: "loading",
        duration: null,
      });

      try {
        const offers = schoolForms.map((result) => {
          if (!result.offers?.[0]?.id) {
            throw new OfferError("One of the selected form(s) has no offer");
          }

          return {
            offer_id: result.offers?.[0].id,
          };
        });

        await asyncForEach(
          _.chunk(offers, DECLINE_BULK_FORM_SCHOOL_OFFER_MAX_INPUT_SIZE),
          (offerChunk) =>
            declineBulkFormSchoolOffer({
              body: offerChunk,
            })
        );

        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: [FSQ.useGetCapacitiesBySchoolAndEnrollmentPeriodKey],
            exact: false,
          }),
          queryClient.invalidateQueries({
            queryKey: [FSQ.useGetCapacitySummaryByEnrollmentPeriodKey],
            exact: false,
          }),
          client.refetchQueries({
            include: [
              FETCH_OFFER_WAITLIST_STATUS,
              GET_ASSIGNED_FORMS_BY_FORM_ID,
            ],
          }),
        ]);
        if (onRefetch) onRefetch();

        toast.close(loadingToastId);
        toast({
          description: `${count} ${offerNoun} declined`,
          status: "info",
        });
        trackFormAction({ verb: "Completed", ...trackArgs });
      } catch (err: any) {
        trackFormActionError(err, trackArgs);
        toast.close(loadingToastId);
        let message = "Check your network and try again.";

        if (err instanceof OfferError) {
          message = err.message;
        }
        toast.error({
          title: "Oops! Something went wrong!",
          description: message,
        });
        console.error(err);
      }
    } else {
      trackFormAction({ verb: "Canceled", ...trackArgs });
    }
  };

  return (
    <WithUserPermissions permissions={["form:update"]}>
      <MenuItem
        textColor="gray.700"
        fontWeight="400"
        fontSize="sm"
        onClick={handleOnClick}
        isDisabled={
          !canDeclineOffers(
            selectedFormSchoolStatuses,
            selectedFormSchools,
            ["both", "admin"].includes(
              formTemplateSettings.allowDeclineAcceptedOffer
            )
          )
        }
      >
        {menuLabel}
      </MenuItem>
      {confirmationDialog}
    </WithUserPermissions>
  );
};
